/**
 * TreatmentPlansPage (Phase 2.7)
 *
 * Top-level route at /org/treatment-plans for doctors + admins to author
 * reusable workflow plans. Replaces the in-visit "+ Save as template"
 * authoring flow from Phase 2.6.
 *
 * Layout:
 *   - Header: title + "+ New plan" button.
 *   - List of the caller's plans (admin sees all org plans with a
 *     "Created by" chip).
 *   - Inline create / edit panel using StepEditor (no separate modal —
 *     fewer context switches for a long form).
 *
 * Permissions:
 *   - DRAFT_TREATMENT_PLAN gates the page (DOCTOR + ADMIN).
 *   - Receptionists / nurses are redirected away by the route guard
 *     in App.js; this component is also defensive (returns null).
 *
 * Day-mode contrast + button visibility rules from CLAUDE.md applied
 * throughout: every button has explicit bg-white / dark:bg-slate-800,
 * tinted panels use solid bg-indigo-50 / bg-amber-50 etc, borders are
 * at least border-slate-300 weight on white surfaces.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';

import AdminPageHeader from '../components/common/AdminPageHeader';
import StepEditor, { emptyStep } from '../components/procedures/StepEditor';
import { usePermissions, usePermission } from '../context/PermissionContext';
import useTreatmentPlanMasterStore from '../stores/treatmentPlanMasterStore';
import useProcedureStore from '../stores/procedureStore';
import useDoctorTeamStore from '../stores/doctorTeamStore';
import useDoctorRateStore from '../stores/doctorRateStore';
import { fetchStaffList } from '../services/api/staffApi';
import {
  formatStaffName,
  formatStaffNameWithRoleHint,
} from '../utils/staffName';


function _normaliseStepForApi(step) {
  // The form holds string inputs; the API expects numbers (Decimal-ish)
  // and proper undefineds for empty optionals. Tooth_numbers is comma-sep.
  const out = {
    procedure_id: step.procedure_id,
    is_mandatory: !!step.is_mandatory,
    discount_amount: Number(step.discount_amount || 0),
    requires_approval: !!step.requires_approval,
  };
  if (step.estimated_cost_override !== '' && step.estimated_cost_override != null) {
    out.estimated_cost_override = Number(step.estimated_cost_override);
  }
  if (step.discount_reason && step.discount_reason.trim().length >= 3) {
    out.discount_reason = step.discount_reason.trim();
  }
  if (step.tooth_numbers && step.tooth_numbers.trim()) {
    out.tooth_numbers = step.tooth_numbers
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (step.default_executor_member_id) {
    out.default_executor_member_id = step.default_executor_member_id;
  }
  if (step.default_approver_member_id && step.requires_approval) {
    out.default_approver_member_id = step.default_approver_member_id;
  }
  if (step.step_id) out.step_id = step.step_id;  // preserve on edit
  return out;
}


function _hydrateStepsForForm(rows = []) {
  return rows.map((s) => ({
    _key: s.step_id || `tmp_${Math.random().toString(36).slice(2, 10)}`,
    step_id: s.step_id,
    procedure_id: s.procedure_id || '',
    is_mandatory: s.is_mandatory !== false,
    estimated_cost_override:
      s.estimated_cost_override != null ? String(s.estimated_cost_override) : '',
    discount_amount:
      s.discount_amount != null && Number(s.discount_amount) > 0
        ? String(s.discount_amount) : '',
    discount_reason: s.discount_reason || '',
    tooth_numbers: Array.isArray(s.tooth_numbers) ? s.tooth_numbers.join(', ') : '',
    default_executor_member_id: s.default_executor_member_id || '',
    requires_approval: !!s.requires_approval,
    default_approver_member_id: s.default_approver_member_id || '',
  }));
}


export default function TreatmentPlansPage() {
  const canDraft = usePermission('DRAFT_TREATMENT_PLAN');
  const { role: sessionRole, memberId: sessionMemberId } = usePermissions();
  const isAdmin = (sessionRole || '').toUpperCase() === 'ADMIN';

  const plans = useTreatmentPlanMasterStore((s) => s.plans);
  const isLoading = useTreatmentPlanMasterStore((s) => s.isLoading);
  const error = useTreatmentPlanMasterStore((s) => s.error);
  const fetchPlans = useTreatmentPlanMasterStore((s) => s.fetchPlans);
  const createPlan = useTreatmentPlanMasterStore((s) => s.createPlan);
  const updatePlan = useTreatmentPlanMasterStore((s) => s.updatePlan);
  const deletePlan = useTreatmentPlanMasterStore((s) => s.deletePlan);

  const procedures = useProcedureStore((s) => s.procedures);
  const fetchProcedures = useProcedureStore((s) => s.fetchProcedures);
  const fetchTeams = useDoctorTeamStore((s) => s.fetchTeams);
  const teamsLoaded = useDoctorTeamStore((s) => s.teamsLoaded);

  // Phase 2.7 fix — load the owner doctor's rate overrides so the per-step
  // effective-rate label can render `override → catalog default` priority.
  // Read-only on this page; standing-rate edits stay on /org/doctor-rates.
  const rates = useDoctorRateStore((s) => s.rates);
  const selectDoctor = useDoctorRateStore((s) => s.selectDoctor);

  const [staff, setStaff] = useState([]);

  // Editor state — null when the page is in list mode; otherwise carries
  // the in-progress draft (either a fresh "create" or an edit of an
  // existing plan).
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!canDraft) return;
    fetchPlans();
    if (!procedures.length) fetchProcedures();
    fetchTeams();
    // Pull the staff roster once for the assignee picker dropdowns.
    // /api/organisation/mem returns List[StaffMemberResponse] directly,
    // so r.data IS the array. Be shape-tolerant in case wrappers change.
    fetchStaffList()
      .then((r) => {
        if (r?.success) {
          const rows = Array.isArray(r.data)
            ? r.data
            : (r.data?.staff || r.data?.members || []);
          const list = rows.map((m) => ({
            member_id: m.member_id,
            role: (m.role || '').toUpperCase(),
            // Full name (first + last). Stash the original member fields so
            // downstream pickers can format with role / specialization hints.
            name: formatStaffName(m),
            first_name: m.first_name,
            last_name: m.last_name,
            specialization: m.specialization,
            username: m.username,
          }));
          setStaff(list);
        }
      })
      .catch(() => { /* non-fatal — picker still has owner-as-self */ });
  }, [canDraft, fetchPlans, fetchProcedures, fetchTeams, procedures.length]);

  // Phase 2.7 fix — load doctor-rate overrides for the relevant owner so
  // StepEditor can show `override → catalog default` per step. Re-fires
  // when the editor opens for a different doctor (admin path).
  const ownerForRates =
    (editing && editing.owner_doctor_member_id) || sessionMemberId || null;
  useEffect(() => {
    if (!canDraft || !ownerForRates) return;
    selectDoctor(ownerForRates);
  }, [canDraft, ownerForRates, selectDoctor]);

  // Form is ready to render once the catalog + the staff roster + the
  // teams fetch have all completed. We need the teams fetch to have
  // resolved (not just teamsByDoctor to be non-empty) so the picker
  // can correctly distinguish:
  //   - doctor has NO team → fall back to all clinic staff (default-open)
  //   - doctor has a team  → restrict options to those members
  // Without `teamsLoaded`, a doctor with a team would briefly see the
  // full clinic roster while the fetch was in flight. Rates is a map;
  // an absent key just means "no override," so it doesn't block.
  const formReady = procedures.length > 0 && staff.length > 0 && teamsLoaded;

  const procedureNameById = useMemo(() => {
    const map = {};
    for (const p of procedures) map[p.procedure_id] = p.name;
    return map;
  }, [procedures]);

  // Doctor roster — derived from the staff list. Used by the admin's
  // "Doctor" picker on create, and the read-only owner label on edit.
  const doctors = useMemo(
    () => staff.filter((s) => s.role === 'DOCTOR'),
    [staff],
  );
  const doctorNameById = useMemo(() => {
    const map = {};
    for (const d of doctors) map[d.member_id] = d.name;
    return map;
  }, [doctors]);

  const startCreate = () => {
    setSubmitError(null);
    setEditing({
      mode: 'create',
      plan_id: null,
      // Doctors are pinned to themselves as the owner. Admins start
      // blank and must pick a doctor (rate overrides resolve against
      // that doctor, and ownership is stamped server-side).
      owner_doctor_member_id: isAdmin ? '' : (sessionMemberId || ''),
      plan_name: '',
      description: '',
      ordering_rule: 'flexible',
      package_discount: '',
      steps: [emptyStep()],
    });
  };

  const startEdit = (plan) => {
    setSubmitError(null);
    setEditing({
      mode: 'edit',
      plan_id: plan.plan_id,
      owner_doctor_member_id: plan.owner_doctor_member_id,
      plan_name: plan.plan_name || '',
      description: plan.description || '',
      ordering_rule: plan.ordering_rule || 'flexible',
      package_discount:
        plan.package_discount != null && Number(plan.package_discount) > 0
          ? String(plan.package_discount) : '',
      steps: _hydrateStepsForForm(plan.steps || []),
    });
  };

  const onCancelEdit = () => {
    setEditing(null);
    setSubmitError(null);
  };

  const onSave = async () => {
    if (!editing) return;
    if (!editing.plan_name.trim()) {
      setSubmitError('Plan name is required.');
      return;
    }
    // Admin must pick a doctor before authoring the plan — backend stamps
    // ownership on that doctor's id, and the doctor's rate overrides
    // resolve against them. Doctors are pinned to themselves at startEdit.
    if (!editing.owner_doctor_member_id) {
      setSubmitError('Pick the doctor this plan is for.');
      return;
    }
    if (editing.steps.length === 0) {
      setSubmitError('At least one step is required.');
      return;
    }
    for (let i = 0; i < editing.steps.length; i++) {
      if (!editing.steps[i].procedure_id) {
        setSubmitError(`Step ${i + 1}: pick a procedure.`);
        return;
      }
    }

    const body = {
      plan_name: editing.plan_name.trim(),
      description: editing.description?.trim() || null,
      ordering_rule: editing.ordering_rule,
      package_discount: Number(editing.package_discount || 0),
      steps: editing.steps.map(_normaliseStepForApi),
    };

    setBusy(true);
    setSubmitError(null);
    try {
      let r;
      if (editing.mode === 'create') {
        // Admin authoring for another doctor: pass owner_doctor_member_id
        // as a query param so the master is stamped with that owner.
        const createOpts =
          isAdmin && editing.owner_doctor_member_id !== sessionMemberId
            ? { ownerDoctorMemberId: editing.owner_doctor_member_id } : {};
        r = await createPlan(body, createOpts);
      } else {
        const opts = isAdmin && editing.owner_doctor_member_id !== sessionMemberId
          ? { memberId: editing.owner_doctor_member_id } : {};
        r = await updatePlan(editing.plan_id, body, opts);
      }
      if (!r.success) {
        setSubmitError(r.error || 'Save failed.');
        return;
      }
      setEditing(null);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (plan) => {
    if (!window.confirm(
      `Delete plan "${plan.plan_name}"? Existing patient plans created from it stay intact.`,
    )) return;
    const opts = isAdmin && plan.owner_doctor_member_id !== sessionMemberId
      ? { memberId: plan.owner_doctor_member_id } : {};
    setBusy(true);
    try {
      const r = await deletePlan(plan.plan_id, opts);
      if (!r.success) window.alert(r.error || 'Delete failed.');
    } finally {
      setBusy(false);
    }
  };

  if (!canDraft) {
    return <Navigate to="/access-denied" replace />;
  }

  const visiblePlans = isAdmin ? plans : plans.filter(
    (p) => p.owner_doctor_member_id === sessionMemberId,
  );

  return (
    <div className="px-4 sm:px-6 py-4 max-w-5xl mx-auto">
      <AdminPageHeader
        title={isAdmin ? 'Treatment Plans (all doctors)' : 'My Treatment Plans'}
        subtitle="Reusable workflow plans you attach to a patient visit."
      />

      {error && (
        <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
          {error}
        </div>
      )}

      {!editing && (
        <>
          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={startCreate}
              className="text-sm font-semibold px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-500"
            >
              + New plan
            </button>
          </div>

          <div className="mt-4 space-y-2">
            {isLoading && (
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Loading…
              </p>
            )}
            {!isLoading && visiblePlans.length === 0 && (
              <div className="text-sm text-slate-600 dark:text-slate-300 italic py-6 text-center border border-dashed border-slate-300 dark:border-slate-600 rounded-md">
                You haven&apos;t created any treatment plans yet. Click
                {' '}<strong>+ New plan</strong> to design one.
              </div>
            )}
            {visiblePlans.map((p) => (
              <div
                key={p.plan_id}
                className="flex flex-wrap items-start gap-3 p-3 rounded-md border-2 border-indigo-400 dark:border-indigo-600 bg-indigo-50 dark:bg-slate-800"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-indigo-900 dark:text-indigo-100">
                    {p.plan_name}
                  </div>
                  <div className="text-xs font-medium text-slate-800 dark:text-slate-200 mt-0.5">
                    {(p.steps || []).length} step{(p.steps || []).length === 1 ? '' : 's'}
                    {' · '}{p.ordering_rule}
                    {Number(p.package_discount) > 0 && (
                      <> · package discount ₹{p.package_discount}</>
                    )}
                  </div>
                  <div className="text-xs text-slate-700 dark:text-slate-300 mt-0.5">
                    Used {p.usage_count || 0} time{(p.usage_count || 0) === 1 ? '' : 's'}
                    {isAdmin && (
                      <span className="ml-2 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-indigo-200 dark:bg-indigo-800 text-indigo-900 dark:text-indigo-100">
                        Created by {p.owner_doctor_member_id}
                      </span>
                    )}
                  </div>
                  {(p.steps || []).length > 0 && (
                    <div className="text-xs text-slate-700 dark:text-slate-300 mt-1 truncate">
                      {(p.steps || []).slice(0, 5).map((s, i) => (
                        <span key={s.step_id} className="mr-2">
                          {i + 1}. {procedureNameById[s.procedure_id] || s.procedure_id}
                          {s.is_mandatory ? '' : ' (opt)'}
                        </span>
                      ))}
                      {(p.steps || []).length > 5 && <>+{p.steps.length - 5} more</>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => startEdit(p)}
                    disabled={busy}
                    className="text-xs font-medium px-2.5 py-1 rounded-md border border-indigo-500 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/40 disabled:opacity-60"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(p)}
                    disabled={busy}
                    className="text-xs font-medium px-2.5 py-1 rounded-md border border-red-400 bg-white dark:bg-slate-800 text-red-700 dark:text-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-60"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {editing && (
        <div className="mt-4 p-4 rounded-md border-2 border-indigo-400 dark:border-indigo-600 bg-white dark:bg-slate-900 space-y-4">
          <div className="text-base font-bold text-indigo-900 dark:text-indigo-100">
            {editing.mode === 'create' ? 'New treatment plan' : `Edit "${editing.plan_name}"`}
          </div>

          {/* Owner doctor — admin picks on create; doctor / edit-mode see it
              read-only. The selected owner drives whose rate-overrides the
              StepEditor uses. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {isAdmin && editing.mode === 'create' ? (
              <label className="block sm:col-span-2">
                <span className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                  Doctor this plan is for
                </span>
                <select
                  value={editing.owner_doctor_member_id || ''}
                  onChange={(e) => setEditing({
                    ...editing,
                    owner_doctor_member_id: e.target.value,
                  })}
                  disabled={busy}
                  className="w-full text-sm font-medium px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 disabled:opacity-60"
                >
                  <option value="">— Select a doctor —</option>
                  {doctors.map((d) => (
                    <option key={d.member_id} value={d.member_id}>
                      {formatStaffNameWithRoleHint(d)}
                    </option>
                  ))}
                </select>
                <span className="block text-xs text-slate-600 dark:text-slate-300 mt-1">
                  Rate overrides shown below resolve against this doctor.
                </span>
              </label>
            ) : (
              <label className="block sm:col-span-2">
                <span className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                  Doctor this plan is for
                </span>
                <input
                  type="text"
                  readOnly
                  value={
                    doctorNameById[editing.owner_doctor_member_id]
                    || editing.owner_doctor_member_id
                    || ''
                  }
                  className="w-full text-sm font-medium px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 cursor-not-allowed"
                />
              </label>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                Plan name
              </span>
              <input
                type="text"
                value={editing.plan_name}
                onChange={(e) => setEditing({ ...editing, plan_name: e.target.value })}
                className="w-full text-sm font-medium px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                placeholder="RCT + Crown"
              />
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                Ordering
              </span>
              <select
                value={editing.ordering_rule}
                onChange={(e) => setEditing({ ...editing, ordering_rule: e.target.value })}
                className="w-full text-sm font-medium px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
              >
                <option value="flexible">Flexible — steps can run in any order</option>
                <option value="strict">Strict — steps must complete in order</option>
              </select>
            </label>
            <label className="block sm:col-span-2">
              <span className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                Description (optional)
              </span>
              <input
                type="text"
                value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                className="w-full text-sm font-medium px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                placeholder="3-visit pathway, e.g."
              />
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                Package discount (₹, optional)
              </span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={editing.package_discount}
                onChange={(e) => setEditing({ ...editing, package_discount: e.target.value })}
                className="w-full text-sm font-medium px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                placeholder="0"
              />
            </label>
          </div>

          <div>
            <div className="text-xs font-bold text-indigo-900 dark:text-indigo-100 mb-2">
              Steps
            </div>
            {formReady ? (
              <StepEditor
                steps={editing.steps}
                onChange={(next) => setEditing({ ...editing, steps: next })}
                procedures={procedures}
                doctorMemberId={editing.owner_doctor_member_id || sessionMemberId}
                doctorName={
                  doctorNameById[
                    editing.owner_doctor_member_id || sessionMemberId
                  ] || null
                }
                staff={staff}
                rates={rates}
                disabled={busy}
              />
            ) : (
              <div className="text-sm text-slate-600 dark:text-slate-300 italic py-4 text-center border border-dashed border-slate-300 dark:border-slate-600 rounded-md">
                Loading procedures and staff…
              </div>
            )}
          </div>

          {editing.mode === 'edit' && (
            <p className="text-xs text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded p-2">
              Editing this plan does NOT change patients already on it —
              they kept the snapshot at attach time.
            </p>
          )}

          {submitError && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {submitError}
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onCancelEdit}
              disabled={busy}
              className="text-sm font-medium px-3 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={busy}
              className="text-sm font-semibold px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-60"
            >
              {busy
                ? 'Saving…'
                : (editing.mode === 'create' ? 'Create plan' : 'Save changes')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
